import React from 'react';
import { Link } from 'react-router-dom';
import { FaShieldAlt, FaMapMarkerAlt, FaCircle, FaTag, FaBuilding, FaUsers, FaChartLine, FaProjectDiagram, FaGraduationCap, FaMobileAlt, FaLayerGroup, FaClipboardList, FaMoneyBillWave, FaBoxes, FaWarehouse, FaUserTie, FaTruck, FaCube, FaRulerCombined, FaTags, FaUserClock } from 'react-icons/fa';
import { AiOutlineFundProjectionScreen } from 'react-icons/ai';
import { useAuth } from '../../provider/auth.provider';

interface MasterCard {
  icon: React.ComponentType<any>;
  label: string;
  description: string;
  path: string;
  resource: string;
  action: string;
  color: string;
  group?: string;
}

const masterCards: MasterCard[] = [
  {
    icon: FaShieldAlt,
    label: 'Roles',
    description: 'Manage user roles and permissions',
    path: '/roles',
    resource: 'Role',
    action: 'view_all',
    color: 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100',
    group: 'Organization',
  },
  {
    icon: FaMapMarkerAlt,
    label: 'States',
    description: 'Manage state circles and regions',
    path: '/states',
    resource: 'StateCircle',
    action: 'view_all',
    color: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100',
    group: 'Organization',
  },
  {
    icon: FaCircle,
    label: 'Circles',
    description: 'Manage circles within states',
    path: '/circles',
    resource: 'StateCircle',
    action: 'view_all',
    color: 'bg-cyan-50 text-cyan-700 border-cyan-200 hover:bg-cyan-100',
    group: 'Organization',
  },
  {
    icon: FaTag,
    label: 'Designations',
    description: 'Manage employee designations and positions',
    path: '/designations',
    resource: 'Designation',
    action: 'view_all',
    color: 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100',
    group: 'Organization',
  },
  {
    icon: FaBuilding,
    label: 'Department Group',
    description: 'Manage departments and organizational units',
    path: '/departments',
    resource: 'Department',
    action: 'view_all',
    color: 'bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100',
    group: 'Organization',
  },
  {
    icon: FaUsers,
    label: 'Clients',
    description: 'Manage client accounts and details',
    path: '/clients',
    resource: 'Client',
    action: 'view_all',
    color: 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100',
    group: 'Business',
  },
  {
    icon: FaChartLine,
    label: 'Client Projects',
    description: 'Manage client project assignments',
    path: '/client-projects',
    resource: 'ClientProject',
    action: 'view_all',
    color: 'bg-pink-50 text-pink-700 border-pink-200 hover:bg-pink-100',
    group: 'Business',
  },
  {
    icon: FaProjectDiagram,
    label: 'Activities',
    description: 'Manage project activity types',
    path: '/activities',
    resource: 'Activity',
    action: 'view_all',
    color: 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100',
    group: 'Business',
  },
  {
    icon: FaUsers,
    label: 'Vendors',
    description: 'Manage vendor accounts and contracts',
    path: '/vendor',
    resource: 'VendorManagement',
    action: 'view_all',
    color: 'bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100',
    group: 'Business',
  },
  {
    icon: AiOutlineFundProjectionScreen,
    label: 'Projects',
    description: 'Manage internal projects',
    path: '/projects',
    resource: 'Project',
    action: 'view_all',
    color: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100',
    group: 'Business',
  },
  {
    icon: FaClipboardList,
    label: 'Project Requirements',
    description: 'Manage project-specific client requirements',
    path: '/requirements/project-specific',
    resource: 'OperationsRequirementsProjectActivity',
    action: 'view_project_specific',
    color: 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100',
    group: 'Business',
  },
  {
    icon: FaLayerGroup,
    label: 'Activity Requirements',
    description: 'Manage activity-wide client requirements',
    path: '/requirements/activity-wide',
    resource: 'OperationsRequirementsProjectActivity',
    action: 'view_activity_wise',
    color: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100',
    group: 'Business',
  },
  {
    icon: FaUsers,
    label: 'Common Requirements',
    description: 'Manage common (all) client requirements',
    path: '/requirements/common',
    resource: 'OperationsRequirementsProjectActivity',
    action: 'view_common_all',
    color: 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100',
    group: 'Business',
  },
  {
    icon: FaBuilding,
    label: 'Training Institutes',
    description: 'Manage training institutes and centers',
    path: '/training/institutes',
    resource: 'TrainingInstitute',
    action: 'view_all',
    color: 'bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100',
    group: 'Training',
  },
  {
    icon: FaGraduationCap,
    label: 'Training Courses',
    description: 'Manage training course types',
    path: '/training/courses',
    resource: 'TrainingType',
    action: 'view_all',
    color: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100',
    group: 'Training',
  },
  {
    icon: FaMobileAlt,
    label: 'App Versions',
    description: 'Manage mobile app releases for Android & iOS',
    path: '/app-versions',
    resource: 'AppVersion',
    action: 'view_all',
    color: 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100',
    group: 'System',
  },
  {
    icon: FaMoneyBillWave,
    label: 'Vendor Invoice Items',
    description: 'Manage reusable invoice item master list',
    path: '/vendor-invoice-items',
    resource: 'VendorInvoiceItem',
    action: 'view_all',
    color: 'bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100',
    group: 'Business',
  },
  {
    icon: FaUserClock,
    label: 'Manual Attendance',
    description: "Allow or disable manual attendance for today's date",
    path: '/attendance/manual-mark-settings',
    resource: 'attendance',
    action: 'manage_manual_mark_settings',
    color: 'bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100',
    group: 'Attendance',
  },
  // Material Management masters - moved here from the sidebar. Each card keeps
  // the view_all permission its route is protected by.
  {
    icon: FaBoxes,
    label: 'Opening Balances',
    description: 'Opening stock batches - Excel import or manual, both need approval',
    path: '/materials/opening-balances',
    resource: 'MaterialOpeningBalance',
    action: 'view_all',
    color: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100',
    group: 'Material Management',
  },
  {
    icon: FaWarehouse,
    label: 'Warehouses',
    description: 'Stores that hold material stock',
    path: '/materials/warehouses',
    resource: 'MaterialWarehouse',
    action: 'view_all',
    color: 'bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100',
    group: 'Material Management',
  },
  {
    icon: FaUserTie,
    label: 'Suppliers',
    description: 'Material suppliers used on arrangements (MAP)',
    path: '/materials/suppliers',
    resource: 'MaterialSupplier',
    action: 'view_all',
    color: 'bg-lime-50 text-lime-700 border-lime-200 hover:bg-lime-100',
    group: 'Material Management',
  },
  {
    icon: FaTruck,
    label: 'Transporters',
    description: 'Transporters that move material between holders',
    path: '/materials/transporters',
    resource: 'MaterialTransporter',
    action: 'view_all',
    color: 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100',
    group: 'Material Management',
  },
  {
    icon: FaCube,
    label: 'Items',
    description: 'Material item master',
    path: '/materials/items',
    resource: 'MaterialItem',
    action: 'view_all',
    color: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100',
    group: 'Material Management',
  },
  {
    icon: FaTags,
    label: 'Classification Categories',
    description: 'Top-level material classification',
    path: '/materials/classification-categories',
    resource: 'MaterialClassificationCategory',
    action: 'view_all',
    color: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200 hover:bg-fuchsia-100',
    group: 'Material Management',
  },
  {
    icon: FaLayerGroup,
    label: 'Sub Categories',
    description: 'Material classification sub categories',
    path: '/materials/classification-subcategories',
    resource: 'MaterialClassificationSubCategory',
    action: 'view_all',
    color: 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100',
    group: 'Material Management',
  },
  {
    icon: FaRulerCombined,
    label: 'Units',
    description: 'Units of measure for material items',
    path: '/materials/units',
    resource: 'MaterialUnit',
    action: 'view_all',
    color: 'bg-cyan-50 text-cyan-700 border-cyan-200 hover:bg-cyan-100',
    group: 'Material Management',
  },
  {
    icon: FaBuilding,
    label: 'Cost Categories',
    description: 'Cost categories for material spend',
    path: '/materials/cost-categories',
    resource: 'MaterialCostCategory',
    action: 'view_all',
    color: 'bg-stone-50 text-stone-700 border-stone-200 hover:bg-stone-100',
    group: 'Material Management',
  },
];

const MastersPage: React.FC = () => {
  const { hasPermission } = useAuth();

  const visibleCards = masterCards.filter(card => hasPermission(card.resource, card.action));

  const groups = Array.from(new Set(visibleCards.map(c => c.group || 'General')));

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Masters</h1>
        <p className="text-sm text-gray-500 mt-1">Manage all master data configurations</p>
      </div>

      {visibleCards.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <p>You do not have access to any master sections.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {groups.map(group => {
            const groupCards = visibleCards.filter(c => (c.group || 'General') === group);
            return (
              <div key={group}>
                <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">{group}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {groupCards.map(card => (
                    <Link
                      key={card.path}
                      to={card.path}
                      className={`flex items-start gap-4 p-5 border rounded-lg transition-colors ${card.color}`}
                    >
                      <div className="mt-0.5 flex-shrink-0">
                        <card.icon className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="font-semibold text-sm">{card.label}</p>
                        <p className="text-xs mt-0.5 opacity-70">{card.description}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MastersPage;
